import React from 'react';
import {
  BarChart3,
  Boxes,
  CalendarCheck,
  Check,
  CreditCard,
  FileCheck2,
  FileDigit,
  Image,
  MessageSquareText,
  Phone,
  Pill,
  Printer,
  UserRound,
} from 'lucide-react';
import Alt6Layout from '../components/Alt6Layout';
import {
  CtaBand,
  Eyebrow,
  Reveal,
  SectionHead,
  TextLink,
} from '../components/Alt6Shared';
import { SOFTWARE_MODULES } from '../data/site';

const MODULE_ICONS = [
  CalendarCheck,
  Phone,
  MessageSquareText,
  FileDigit,
  UserRound,
  Image,
  FileCheck2,
  CreditCard,
  Boxes,
  Pill,
  Printer,
  BarChart3,
];

export default function Alt6Platform() {
  return (
    <Alt6Layout title="Platform">
      <section className="a6-page-hero">
        <div className="a6-container a6-page-hero-inner">
          <Reveal>
            <Eyebrow>Platform</Eyebrow>
          </Reveal>
          <Reveal delay={0.06}>
            <h1>
              One system for <em>everything</em> your office runs on
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p>
              Asha isn't an add-on that sits beside your PMS. It <strong>is</strong>{' '}
              the PMS — plus the phones, forms, imaging, claims, payments, and
              messaging that usually live in five other tabs. One login, one patient
              record, one place where work actually happens.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="a6-section">
        <div className="a6-container">
          <div className="a6-grid a6-grid-3">
            {SOFTWARE_MODULES.map((mod, i) => {
              const Icon = MODULE_ICONS[i % MODULE_ICONS.length];
              return (
                <Reveal key={mod.name} delay={(i % 3) * 0.06}>
                  <div className="a6-card">
                    <span className="a6-card-icon">
                      <Icon size={20} strokeWidth={2.2} />
                    </span>
                    <h3>{mod.name}</h3>
                    <p>{mod.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="a6-section a6-wash">
        <div className="a6-container">
          <div className="a6-split">
            <Reveal className="a6-split-copy">
              <Eyebrow>Why it matters</Eyebrow>
              <h2>AI-native isn't a feature. It's the architecture.</h2>
              <p>
                When the schedule, the ledger, the phones, and the chart live in one
                system, AI doesn't need brittle integrations to act — it books the
                appointment, posts the payment, and updates the chart directly. That's
                the difference between a bolted-on assistant and an operating system.
              </p>
              <ul className="a6-split-list">
                {[
                  ['One patient record', 'every call, form, image, claim, and message in one timeline'],
                  ['No integration tax', 'nothing to sync, nothing to double-enter, nothing to reconcile'],
                  ['Cloud from day one', 'no server closet, no bridge software, secure from anywhere'],
                ].map(([strong, rest]) => (
                  <li key={strong}>
                    <Check size={18} strokeWidth={2.6} />
                    <span>
                      <strong>{strong}</strong> — {rest}
                    </span>
                  </li>
                ))}
              </ul>
              <TextLink to="/alt6/autopilot">See what the AI does with it</TextLink>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="a6-band" style={{ padding: '52px 44px' }}>
                <h2 className="a6-h2" style={{ fontSize: 30 }}>
                  Replace the patchwork.
                </h2>
                <p className="a6-lede" style={{ fontSize: 16.5 }}>
                  The typical practice signs into a PMS, a phone system, a forms tool,
                  a claims portal, a payments terminal, a fax service, and a reporting
                  dashboard — none of which agree on what happened today. Asha
                  collapses the stack into one system that always does.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBand
        title="See the whole platform in one walkthrough."
        body="Thirty minutes, your workflows — from the morning huddle to the last claim of the day."
      />
    </Alt6Layout>
  );
}
